import cx from 'classnames'
import { type Dispatch, type SetStateAction, useContext, useMemo } from 'react'

import { isNumeric } from '../lib/helpers'
import { formatPrice } from '../lib/price'
import { FrameBuilderShopContext } from '../contexts/shop-context'
import { FrameBuilderStringsContext } from '../contexts/strings-context'

interface PricingControlsProps {
  price: number
  quantity: string
  setQuantity: Dispatch<SetStateAction<string>>
  isQuantityValid: boolean
  setIsQuantityValid: Dispatch<SetStateAction<boolean>>
  className?: string
}

const PricingControls = ({
  price,
  quantity,
  setQuantity,
  isQuantityValid,
  setIsQuantityValid,
  className,
}: PricingControlsProps) => {
  const { currency, taxRate } = useContext(FrameBuilderShopContext)
  const frameBuilderStrings = useContext(FrameBuilderStringsContext)

  const priceString = useMemo(() => {
    if (!price && price !== 0) {
      return
    }

    const priceWithTax = price * (1 + taxRate)

    return [currency, formatPrice(priceWithTax)].filter(Boolean).join(' ')
  }, [currency, taxRate, price])

  const handleQuantityChange = (value: string) => {
    setQuantity(value)

    // Validate new quantity
    const isValid = isNumeric(value) && Number(value) >= 1
    setIsQuantityValid(isValid)
  }

  return (
    <div
      className={cx(
        'flex justify-stretch items-stretch px-3 bg-white',
        className,
      )}
    >
      <div className="flex-1 flex justify-center items-center pb-1">
        <label
          htmlFor="frame-builder-product-quantity"
          className="text-sm uppercase"
        >
          {frameBuilderStrings.controlQuantityLabel}
        </label>

        <input
          id="frame-builder-product-quantity"
          type="number"
          min="1"
          max="999"
          step="1"
          value={quantity}
          onChange={(event) => handleQuantityChange(event.target.value)}
          className={cx('max-w-[80px] ml-2 py-2 px-2 border rounded-lg', {
            'border-gray-400': isQuantityValid,
            'border-error': !isQuantityValid,
          })}
        />
      </div>

      <div className="grow-0 ml-3 border-l border-gray-300" />

      <div className="flex-1 flex justify-center ml-3">
        <div className="flex flex-col justify-center">
          {priceString && <span className="text-xl">{priceString}</span>}

          <div className="mt-[2px] text-sm">
            {frameBuilderStrings.controlPriceLabel}
          </div>
        </div>
      </div>
    </div>
  )
}

export default PricingControls
